export const LINE_SEARCH_FIELDS = [
  "all",
  "variant",
  "product",
  "category",
  "color",
  "sku",
  "store",
] as const

export type LineSearchField = (typeof LINE_SEARCH_FIELDS)[number]

/** The fields a table of variants and SKUs can be searched by: every one but the store. */
export const PRODUCT_REPORT_SEARCH_FIELDS = LINE_SEARCH_FIELDS.filter(
  (field): field is Exclude<LineSearchField, "store"> => field !== "store",
) as readonly Exclude<LineSearchField, "store">[] as readonly [
  Exclude<LineSearchField, "store">,
  ...Exclude<LineSearchField, "store">[],
]

const LINE_SEARCH_LABELS: Record<LineSearchField, string> = {
  all: "All",
  variant: "Variant",
  product: "Product",
  category: "Category",
  color: "Colour",
  sku: "SKU",
  store: "Store",
}

export type LineSearchOption<F extends LineSearchField = LineSearchField> = {
  value: F
  label: string
}

export const LINE_SEARCH_OPTIONS: LineSearchOption[] = LINE_SEARCH_FIELDS.map((value) => ({
  value,
  label: LINE_SEARCH_LABELS[value],
}))

export const VARIANT_SEARCH_OPTIONS = LINE_SEARCH_OPTIONS.filter(
  (option): option is LineSearchOption<Exclude<LineSearchField, "store">> =>
    option.value !== "store",
)

export function toLineSearchField<F extends LineSearchField>(
  value: string,
  options: readonly LineSearchOption<F>[],
): F
export function toLineSearchField(value: string): LineSearchField
export function toLineSearchField(
  value: string,
  options: readonly LineSearchOption[] = LINE_SEARCH_OPTIONS,
): LineSearchField {
  return options.find((option) => option.value === value)?.value ?? "all"
}

/** A row's searchable fields; a variant row lists its sizes' SKU ids. */
export type LineSearchRow = {
  productId: string
  productName: string
  variantId: string
  variantName?: string | null
  category?: string | null
  color?: string | null
  skuIds: readonly string[]
  sizes?: readonly (string | null)[]
  storeName?: string | null
  storeExternalId?: string | null
}

type SearchText = string | null | undefined

function variantText(row: LineSearchRow): string {
  return [row.productName, row.variantName].filter((part) => part != null).join(" ")
}

function searchedTexts(row: LineSearchRow, searchBy: LineSearchField): readonly SearchText[] {
  switch (searchBy) {
    case "variant":
      return [variantText(row), row.variantId]
    case "product":
      return [row.productName, row.productId]
    case "category":
      return [row.category]
    case "color":
      return [row.color]
    case "sku":
      return row.skuIds
    case "store":
      return [row.storeName, row.storeExternalId]
    case "all":
      return [
        variantText(row),
        row.variantId,
        row.productId,
        row.storeName,
        row.storeExternalId,
        row.category,
        row.color,
        ...(row.sizes ?? []),
        ...row.skuIds,
      ]
  }
}

/**
 * Case-insensitive substring search over the fields each option names, mirroring the
 * server's line search. Pass `toSearchRow` when the row's own shape differs.
 */
export function matchesLineSearch(
  row: LineSearchRow,
  search: string,
  searchBy: LineSearchField,
): boolean
export function matchesLineSearch<Row>(
  row: Row,
  search: string,
  searchBy: LineSearchField,
  toSearchRow: (row: Row) => LineSearchRow,
): boolean
export function matchesLineSearch(
  row: unknown,
  search: string,
  searchBy: LineSearchField,
  toSearchRow: (row: unknown) => LineSearchRow = (searchRow) => searchRow as LineSearchRow,
): boolean {
  const needle = search.trim().toLowerCase()
  if (needle === "") return true
  return searchedTexts(toSearchRow(row), searchBy).some(
    (text) => text?.toLowerCase().includes(needle) ?? false,
  )
}
